// #PER_DIEM_V1 (2026-10-21) — Bill: per diem is paid for every night in a hotel the PM books (hotel-requests), EXCEPT
// courtesy rooms near Ennis ("these guys get tired at night and want to stay local… that does not get a per diem").
//   rate        $55 a night, fixed — never changes
//   big rig     NOT here — #BIGRIG_PERK_V1 (2026-10-21) Bill: the $35 Big Rig perk is paid every day the rig has a Daily
//               report, whole crew, not tied to hotel. It lives on Payroll › Big Rig (BigRigPerk.js); the hotel shows a note only.
//   state add   +$15 a night only in states on PER_DIEM_STATE_ADD (Kansas, Mississippi — one-off tax offsets)
//   courtesy    $0 when the hotel's town is within PER_DIEM_RADIUS_MI of Ennis (Bill 10/21: 25 miles)
//   nights      a night = a date D with in <= D < out on a booked (confirmed) stay
//   override    none — Bill 10/21: the nightly amount is set and does not change; Big Rig add is the only exception
// Towns are measured from a built-in table (below). A town not on it counts as OUT of the radius (pays per diem) —
// add a town here if a courtesy hotel ever shows up paying.
export const PER_DIEM_RATE = 55;
export const PER_DIEM_BIG_RIG = 35;
export const BIG_RIG_RE = /(220|330)\s*[x×]\s*500/i;
export const PER_DIEM_STATE_ADD = { KS: 15, MS: 15 };
export const PER_DIEM_RADIUS_MI = 25;
export const HOTEL_FIELD_KEY = 'hotel-field';

const ENNIS = [32.3293, -96.6253];
// [lat, lng] — towns around Ennis (some beyond 25 mi on purpose, so changing the radius works)
const NEAR = {
  ENNIS: [32.3293, -96.6253], PALMER: [32.4254, -96.6672], FERRIS: [32.5343, -96.6656], BARDWELL: [32.2682, -96.6969],
  GARRETT: [32.3607, -96.6519], ALMA: [32.2843, -96.5517], RICE: [32.2432, -96.4983], CORSICANA: [32.0954, -96.4689],
  ITALY: [32.1840, -96.8847], WAXAHACHIE: [32.3865, -96.8483], 'RED OAK': [32.5176, -96.8044], 'OAK LEAF': [32.5207, -96.8544],
  OVILLA: [32.5265, -96.8864], LANCASTER: [32.5921, -96.7561], WILMER: [32.5890, -96.6853], HUTCHINS: [32.6493, -96.7131],
  COMBINE: [32.5885, -96.5086], SEAGOVILLE: [32.6393, -96.5386], CRANDALL: [32.6279, -96.4555], SCURRY: [32.5157, -96.3897],
  ROSSER: [32.4626, -96.4531], KEMP: [32.4426, -96.2297], KAUFMAN: [32.5890, -96.3089], MABANK: [32.3660, -96.1008],
  KERENS: [32.1332, -96.2275], MIDLOTHIAN: [32.4824, -96.9945], MAYPEARL: [32.3132, -97.0089], MILFORD: [32.1218, -96.9447],
  FROST: [32.0782, -96.8094], BLOOMING_GROVE: [32.0921, -96.7175], 'BLOOMING GROVE': [32.0921, -96.7175], DAWSON: [31.8955, -96.7139],
  MERTENS: [32.0590, -96.8919], AVALON: [32.2185, -96.7883], 'DESOTO': [32.5899, -96.8570], 'DUNCANVILLE': [32.6518, -96.9083],
  'CEDAR HILL': [32.5885, -96.9561], MESQUITE: [32.7668, -96.5992], FORNEY: [32.7482, -96.4719], TERRELL: [32.7360, -96.2753],
  ATHENS: [32.2049, -95.8555], HILLSBORO: [32.0110, -97.1300], DALLAS: [32.7767, -96.7970],
};
const toRad = (d) => d * Math.PI / 180;
export const milesBetween = (a, b) => {
  const R = 3958.8, dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const townOf = (city) => String(city || '').toUpperCase().split(',')[0].replace(/[^A-Z ]/g, '').replace(/\s+/g, ' ').trim();
// Miles from Ennis for a TX town on the table, else null (unknown = treated as out of town).
export const milesFromEnnis = (city, st) => {
  const s = String(st || '').trim().toUpperCase();
  if (s && s !== 'TX') return null;
  const p = NEAR[townOf(city)];
  return p ? milesBetween(ENNIS, p) : null;
};

const ST = { TEXAS: 'TX', KANSAS: 'KS', MISSISSIPPI: 'MS', LOUISIANA: 'LA', OKLAHOMA: 'OK', ARKANSAS: 'AR', 'NEW MEXICO': 'NM' };
export const stateOf = (city, st) => {
  const s = String(st || '').trim().toUpperCase(); if (/^[A-Z]{2}$/.test(s)) return s;
  const m = String(city || '').toUpperCase().match(/,\s*([A-Z .]+)\s*$/); if (!m) return '';
  const t = m[1].trim(); return /^[A-Z]{2}$/.test(t) ? t : (ST[t] || '');
};

const pad2 = (n) => String(n).padStart(2, '0');
const isoAdd = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

// One hotel request → the stay as the field sees it: where, nights, and the per diem for each night.
export const stayOf = (r, rig) => {
  const b = (Array.isArray(r.bookings) && r.bookings[0]) || {};
  const city = b.city || r.stayCity || r.city || '';
  const st = stateOf(b.city ? `${b.city}, ${b.st || ''}` : (r.stayCity || r.city), b.st || r.staySt);
  const mi = milesFromEnnis(city, st);
  const courtesy = mi != null && mi <= PER_DIEM_RADIUS_MI;
  const big = BIG_RIG_RE.test(String(rig || ''));
  const rate = courtesy ? 0 : PER_DIEM_RATE + (PER_DIEM_STATE_ADD[st] || 0);   // #BIGRIG_PERK_V1 — Big Rig $35 paid separately, by Daily report day
  const nights = [];
  if (r.in && r.out) for (let d = r.in; d < r.out && nights.length < 60; d = isoAdd(d, 1)) nights.push(d);
  return { id: r.id, in: r.in, out: r.out, hotel: b.hotel || '', addr: b.addr || '', conf: b.conf || '', city, st,
    duc: r.displayDuc || r.duc || '', pm: r.jobPm || r.pm || '', status: r.status || '', nights, rate, courtesy, miles: mi, big,
    why: courtesy ? `Courtesy room — ${Math.round(mi)} mi from Ennis, no per diem` : `$55${PER_DIEM_STATE_ADD[st] ? ` + $${PER_DIEM_STATE_ADD[st]} ${st}` : ''} a night${big ? ' · Big Rig $35/day paid separately on report days' : ''}` };
};

// Slim copy of hotel-requests the field may read: booked stays from the last 45 days on, no costs / estimates.
export const slimHotelField = (reqs) => {
  const cut = isoAdd(new Date().toISOString().slice(0, 10), -45);
  return (Array.isArray(reqs) ? reqs : [])
    .filter(r => r && r.out && r.out >= cut && /confirm|book/i.test(String(r.status || '')))
    .map(r => ({ id: r.id, who: r.who || '', in: r.in || '', out: r.out || '', status: r.status || '', duc: r.duc || '', displayDuc: r.displayDuc || '',
      pm: r.pm || '', jobPm: r.jobPm || '', city: r.city || '', stayCity: r.stayCity || '', staySt: r.staySt || '', rooms: r.rooms || 1,
      bookings: (Array.isArray(r.bookings) ? r.bookings : []).map(b => ({ hotel: b.hotel || '', addr: b.addr || '', city: b.city || '', st: b.st || '', conf: b.conf || '' })) }));
};

// Does a request's "who" (free text: "John Sen", "Sen, John", "CODY") belong to this person?
const nk = (s) => String(s || '').toUpperCase().replace(/[^A-Z ,]/g, '').replace(/\s+/g, ' ').trim();
export const hotelIsFor = (who, fullName, fieldName) => {
  const w = nk(who); if (!w) return false;
  const flip = w.includes(',') ? w.split(',').map(x => x.trim()).reverse().join(' ') : w;
  const full = nk(fullName), fn = nk(fieldName);
  if (full && flip === full) return true;
  const fp = full.split(' '), wp = flip.split(' ');
  if (fp.length > 1 && wp.length > 1 && wp[0] === fp[0] && wp[wp.length - 1][0] === fp[fp.length - 1][0]) return true;   // "John S" / "John Sen"
  if (fn && (flip === fn || wp[0] === fn.split(' ')[0] && wp.length === 1)) return true;   // "CODY"
  return false;
};
